import React from 'react';
import { formatDateReport } from '@app/utils/formatDateReport';
import { formatReportTitle } from '@app/utils/formatReportTitle';

import { Space, Dropdown, Typography, type TableColumnsType } from '@app/ui';

interface ReportsDataType {
    key: React.Key;
    addedAt: string;
    goal: string;
    startDate: string;
    endDate: string;
    userEmail: string;
    useRightsholderAttribution: boolean;
}

const formatDataIncluded = (record: any) => {
    const reportTitle = record.goalName
        ? record.goalName
        : formatReportTitle(
              record.rightsholderName,
              record.sponsorName,
              record.placementName,
          );
    const broadcastCountries = Array.isArray(record.broadcastCountryNames)
        ? record.broadcastCountryNames
              .filter(
                  (name: unknown): name is string =>
                      typeof name === 'string' && Boolean(name.trim()),
              )
              .map(name => name.trim())
        : [];

    return broadcastCountries.length
        ? `${reportTitle} - ${broadcastCountries.join(', ')}`
        : reportTitle;
};

export const getHistoryColumns = ({
    onRepopulate,
    onDownload,
    onDelete,
    showMethodologyColumn,
    t,
}: {
    onRepopulate: (row: any) => void;
    onDownload: (row: any) => Promise<void>;
    onDelete: (row: any) => void;
    showMethodologyColumn: boolean;
    t: (key: string) => string;
}) => {
    const columns: TableColumnsType<ReportsDataType> = [
        {
            title: t('reports.tables.history.columns.dateCreated'),
            dataIndex: 'addedAt',
            render: (value: string) => formatDateReport(value),
            sorter: (a: any, b: any) =>
                new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime(),
            width: 120,
            minWidth: 80,
        },
        {
            title: t('reports.tables.history.columns.reportType'),
            dataIndex: 'goal',
            render: (_: any, record: any) =>
                record.goal
                    ? t('reports.tables.history.values.pacing')
                    : t('reports.tables.history.values.standard'),
            sorter: (a: any, b: any) => {
                const aGoal = a.goal ? 'Pacing' : 'Standard';
                const bGoal = b.goal ? 'Pacing' : 'Standard';
                return aGoal.localeCompare(bGoal);
            },
            width: 100,
            minWidth: 80,
        },
        {
            title: t('reports.tables.history.columns.dataIncluded'),
            render: (_: any, record: any) => formatDataIncluded(record),
            width: 300,
            minWidth: 200,
        },
        {
            title: t('reports.tables.history.columns.dateFrom'),
            dataIndex: 'startDate',
            render: (value: string) => formatDateReport(value),
            sorter: (a: any, b: any) =>
                new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime(),
            width: 120,
            minWidth: 80,
        },
        {
            title: t('reports.tables.history.columns.dateTo'),
            dataIndex: 'endDate',
            render: (value: string) => formatDateReport(value),
            sorter: (a: any, b: any) =>
                new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime(),
            width: 120,
            minWidth: 80,
        },
        {
            title: t('reports.tables.history.columns.createdBy'),
            dataIndex: 'userEmail',
            render: (value: string) => (
                <Typography.Text style={{ color: '#009FDF' }}>
                    {value}
                </Typography.Text>
            ),
            sorter: (a: any, b: any) => a.userEmail.localeCompare(b.userEmail),
            width: 150,
            minWidth: 100,
            ellipsis: true,
        },
        {
            title: t('reports.tables.history.columns.methodology'),
            dataIndex: 'useRightsholderAttribution',
            // If the record does not have useRightsholderAttribution, the report was created before the RH attribution type implementation
            // so it should have the default value (Team Attribution)
            render: (_: any, { useRightsholderAttribution = false }: any) =>
                useRightsholderAttribution
                    ? t('reports.tables.history.values.leagueAttribution')
                    : t('reports.tables.history.values.teamAttribution'),
            sorter: (a: any, b: any) => {
                const aValue = a.useRightsholderAttribution ?? false;
                const bValue = b.useRightsholderAttribution ?? false;
                // Convert booleans to numbers for comparison (true = 1, false = 0)
                return Number(aValue) - Number(bValue);
            },
            width: 180,
            minWidth: 150,
        },
        {
            title: '',
            dataIndex: 'actions',
            align: 'center',
            render: (_: any, record: any) => {
                const items = [
                    {
                        key: 'download',
                        label: t('reports.tables.history.actions.download'),
                        icon: <i className="fa-regular fa-download" />,
                        onClick: () => onDownload(record),
                    },
                    {
                        key: 'repopulate',
                        label: t('reports.tables.history.actions.repopulate'),
                        icon: <i className="fa-regular fa-rotate" />,
                        onClick: () => onRepopulate(record),
                    },
                    {
                        key: 'delete',
                        label: t('reports.tables.history.actions.delete'),
                        icon: <i className="fa-regular fa-trash" />,
                        onClick: () => onDelete(record),
                    },
                ];

                return (
                    <Space size="middle">
                        <Dropdown
                            trigger={['hover']}
                            menu={{
                                items: items.map(
                                    ({ key, label, icon, onClick }) => ({
                                        key,
                                        label: (
                                            <div
                                                onClick={onClick}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 8,
                                                }}
                                            >
                                                {icon}
                                                {label}
                                            </div>
                                        ),
                                    }),
                                ),
                            }}
                        >
                            <a>
                                <i
                                    className="fa-solid fa-ellipsis-vertical"
                                    style={{ fontSize: 16, color: '#009FDF' }}
                                />
                            </a>
                        </Dropdown>
                    </Space>
                );
            },
            width: 50,
            minWidth: 50,
            fixed: 'right',
        },
    ];

    // Filter out Methodology column when it should not be displayed.
    const filteredColumns = showMethodologyColumn
        ? columns
        : columns.filter(
              col =>
                  !('dataIndex' in col) ||
                  col.dataIndex !== 'useRightsholderAttribution',
          );

    return filteredColumns;
};
